/**
 * Interface primitives.
 *
 * Every one of these is mobile-first: touch targets are at least 44px, text
 * inputs are 16px on small screens (below that, iOS Safari zooms the page on
 * focus), and nothing assumes a hover state exists.
 */

import { type ReactNode, type ButtonHTMLAttributes, useEffect, useRef } from 'react';
import { cn } from '../lib/cn';

// --- Card -------------------------------------------------------------------

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn('oc-card p-4 sm:p-5', className)}>{children}</div>;
}

export function CardHeader({ title, subtitle, action }: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    // Wraps on narrow screens so a long heading does not squeeze its action
    // button into an unreadable column.
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 mb-4">
      <div className="min-w-0">
        {/* Wraps rather than truncates: these headings carry meaning, and a
            clipped "Open gaps by sev…" is worse than a second line. */}
        <h2 className="oc-h2 text-balance">{title}</h2>
        {subtitle && <p className="oc-muted mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// --- Button -----------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'secondary',
  size,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: 'sm' }) {
  const variants: Record<ButtonVariant, string> = {
    primary: 'oc-btn-primary',
    secondary: 'oc-btn-secondary',
    ghost: 'oc-btn-ghost',
    danger: 'oc-btn-danger',
  };
  return (
    <button
      type="button"
      className={cn(variants[variant], size === 'sm' && 'oc-btn-sm', className)}
      {...props}
    >
      {children}
    </button>
  );
}

// --- Status pill ------------------------------------------------------------

export type Tone = 'good' | 'warning' | 'serious' | 'critical' | 'neutral';

/**
 * Maps a domain status to a tone.
 *
 * Centralised so a status looks the same everywhere, and so the reserved status
 * colours are never applied to anything that is not a status.
 */
export const TONE_FOR_STATUS: Record<string, Tone> = {
  // Compliance verdicts
  COMPLIANT: 'good', NEEDS_IMPROVEMENT: 'warning', NON_COMPLIANT: 'critical',
  NOT_APPLICABLE: 'neutral',
  // Gap / submission
  OPEN: 'warning', CLOSED: 'good', DRAFT: 'neutral', FINALIZED: 'good',
  // A reopened submission is a finalised record being edited again: serious,
  // not neutral, so it reads as "this is not in its committed state".
  REOPENED: 'serious',
  // Plans
  NOT_STARTED: 'neutral', IN_PROGRESS: 'good', AT_RISK: 'serious',
  DONE: 'good', CANCELLED: 'neutral',
  // Priority / risk
  LOW: 'good', MEDIUM: 'warning', HIGH: 'serious', CRITICAL: 'critical',
  // Controls
  APPLIED: 'good', NOT_APPLIED: 'critical', PENDING: 'warning', NOT_REVIEWED: 'neutral',
  // Approvals
  NOT_REQUIRED: 'neutral', APPROVED: 'good', REJECTED: 'critical',
};

/**
 * A status pill.
 *
 * Always renders a text label alongside the colour — colour alone is invisible
 * to a colour-blind reader and to anyone printing in greyscale. The dot is a
 * reinforcement, not the message.
 */
export function Pill({ tone = 'neutral', children, className }: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}) {
  const classes: Record<Tone, string> = {
    good: 'oc-pill-good',
    warning: 'oc-pill-warning',
    serious: 'oc-pill-serious',
    critical: 'oc-pill-critical',
    neutral: 'oc-pill-neutral',
  };
  return (
    <span className={cn(classes[tone], className)}>
      <span
        aria-hidden
        className={cn(
          'w-1.5 h-1.5 rounded-full shrink-0',
          tone === 'good' && 'bg-good',
          tone === 'warning' && 'bg-warning',
          tone === 'serious' && 'bg-serious',
          tone === 'critical' && 'bg-critical',
          tone === 'neutral' && 'bg-ink-muted',
        )}
      />
      {children}
    </span>
  );
}

// --- Stat tile --------------------------------------------------------------

/**
 * A single headline number.
 *
 * When the data's job is "one number", a chart adds nothing — the number IS the
 * visualisation. `tabular-nums` keeps digits from shifting as values update.
 */
export function StatTile({ label, value, hint, tone, icon }: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  icon?: ReactNode;
}) {
  return (
    <div className="oc-card p-4 flex flex-col gap-1">
      <div className="flex items-center gap-2 text-ink-secondary">
        {icon}
        <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
      </div>
      <div
        className={cn(
          'text-2xl sm:text-3xl font-semibold tabular-nums text-ink',
          tone === 'critical' && 'text-critical',
          tone === 'serious' && 'text-serious',
          tone === 'good' && 'text-good',
        )}
      >
        {value}
      </div>
      {hint && <div className="text-xs text-ink-muted">{hint}</div>}
    </div>
  );
}

// --- Empty / loading / error states ------------------------------------------

export function EmptyState({ title, description, action, icon }: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="text-center py-12 px-4">
      {icon && <div className="mx-auto mb-3 text-ink-muted">{icon}</div>}
      <p className="font-medium text-ink">{title}</p>
      {description && <p className="oc-muted mt-1 max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin h-5 w-5 text-ink-muted', className)}
      viewBox="0 0 24 24"
      fill="none"
      role="status"
      aria-label="Loading"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M12 2a10 10 0 0 1 10 10h-3a7 7 0 0 0-7-7V2Z" />
    </svg>
  );
}

/** Skeleton placeholder, sized like the content it stands in for. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-lg bg-ink/[0.06]', className)} />;
}

export function ErrorNotice({ message, onRetry, retryLabel }: {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-critical/30 bg-critical/[0.06] px-4 py-3 flex items-start gap-3"
    >
      <svg viewBox="0 0 20 20" className="w-5 h-5 text-critical shrink-0 mt-0.5" fill="currentColor" aria-hidden>
        <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 4a1 1 0 011 1v4a1 1 0 11-2 0V7a1 1 0 011-1zm0 8.5a1.25 1.25 0 110-2.5 1.25 1.25 0 010 2.5z" />
      </svg>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-ink">{message}</p>
        {onRetry && (
          <button onClick={onRetry} className="mt-2 text-sm font-medium text-brand hover:underline">
            {retryLabel ?? 'Retry'}
          </button>
        )}
      </div>
    </div>
  );
}

// --- Modal ------------------------------------------------------------------

/**
 * A dialog.
 *
 * Renders as a bottom sheet on phones and a centred dialog from `sm` up —
 * a bottom sheet is reachable with a thumb, a centred modal is not.
 * Escape closes it, the backdrop closes it, and focus moves into the dialog on
 * open so keyboard and screen-reader users are not left behind on the page.
 */
export function Modal({ open, onClose, title, children, footer }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind the dialog from scrolling under it.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="relative w-full sm:max-w-lg max-h-[92dvh] overflow-y-auto
                   bg-surface-raised border border-line
                   rounded-t-2xl sm:rounded-2xl shadow-raised
                   animate-fade-in outline-none
                   pb-[env(safe-area-inset-bottom)]"
      >
        {/* Grab handle: signals "this sheet can be dismissed" on touch. */}
        <div className="sm:hidden flex justify-center pt-3" aria-hidden>
          <div className="w-10 h-1 rounded-full bg-ink/15" />
        </div>
        <div className="px-4 sm:px-5 py-4 border-b border-line flex items-center justify-between gap-3">
          <h2 className="oc-h2 truncate">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-2 -mr-2 rounded-lg text-ink-secondary hover:bg-surface-sunken"
          >
            <svg viewBox="0 0 20 20" className="w-5 h-5" fill="currentColor" aria-hidden>
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>
        <div className="px-4 sm:px-5 py-4">{children}</div>
        {footer && (
          <div className="px-4 sm:px-5 py-4 border-t border-line flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Form field -------------------------------------------------------------

export function Field({ label, hint, error, required, children, htmlFor }: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="mb-4">
      <label className="oc-label" htmlFor={htmlFor}>
        {label}
        {required && <span className="text-critical ml-0.5" aria-hidden>*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
      {/* role="alert" so a screen reader announces the problem when it appears. */}
      {error && <p role="alert" className="mt-1 text-xs text-critical">{error}</p>}
    </div>
  );
}

// --- Progress ---------------------------------------------------------------

/**
 * A magnitude bar.
 *
 * Thin mark, rounded data-end, recessive track, and the value stated as text
 * beside it — so the number is readable without measuring the bar.
 */
export function ProgressBar({ value, tone = 'brand', label }: {
  /** 0–100. */
  value: number;
  tone?: 'brand' | Tone;
  label?: string;
}) {
  const clamped = Math.max(0, Math.min(100, value));
  const fill: Record<string, string> = {
    brand: 'bg-brand', good: 'bg-good', warning: 'bg-warning',
    serious: 'bg-serious', critical: 'bg-critical', neutral: 'bg-ink-muted',
  };
  return (
    <div
      className="flex items-center gap-3"
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className="flex-1 h-2 rounded-full bg-ink/[0.08] overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-[width] duration-500', fill[tone])}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-sm font-medium tabular-nums text-ink w-11 text-right">{clamped}%</span>
    </div>
  );
}
